import React from 'react';
import { Box } from '@mui/material';

import { InformationBlock, InformationBlockProps, Page } from 'components';
import { formatDate } from 'core/shared';
import { useParams } from 'hooks/useParams';
import { useTranslations } from 'hooks/useTranslations';

import { getLabelColor, getStatus } from 'config/shared';
import { UseMutation, UseQuery } from 'useQuery';

function TransactionPage() {
  const { id } = useParams('transaction');

  const { data: transactionData, isFetching } = UseQuery.transaction({ id });

  const { mutateAsync: mutateAsyncResetTransaction } = UseMutation.resetTransaction();
  const [loading, setLoading] = React.useState(false);

  const [items, setItems] = React.useState<InformationBlockProps['items']>([]);
  const translations = useTranslations('transactionPage');

  React.useEffect(() => {
    if (!transactionData?.result) {
      return;
    }

    const { entity, entity_email, date, price, price_net, category, description, status } =
      transactionData.result;

    setItems([
      { label: translations.fullName, data: { value: entity ?? '' }, type: 'text' },
      { label: translations.email, data: { value: entity_email ?? '' }, type: 'text' },
      {
        label: translations.transactionDate,
        data: {
          value: formatDate({ date: date, variant: 'long' }),
        },
        type: 'text',
      },
      {
        label: translations.priceIncVat,
        data: { value: price ?? 0, adornment: 'EUR' },
        type: 'price',
      },
      {
        label: translations.priceExVat,
        data: { value: price_net ?? 0, adornment: 'EUR' },
        type: 'price',
      },
      { label: translations.category, data: { value: category ?? '' }, type: 'text' },
      { label: translations.description, data: { value: description ?? '' }, type: 'text' },
      {
        label: translations.status,
        data: {
          value: getStatus({ transaction: status }),
          color: getLabelColor({ transaction: status }),
        },
        type: 'label',
      },
    ]);
  }, [transactionData]);

  return (
    <Page name="transaction" loading={isFetching || !transactionData}>
      <>
        <InformationBlock
          items={items}
          actions={[
            {
              title: translations.resetToPending,
              svgName: 'restore',
              mode: 'closeAfterLoading',
              onClick: () => {
                setLoading(true);
                mutateAsyncResetTransaction({ id })
                  .then(() => {
                    setLoading(false);
                  })
                  .catch(() => {
                    setLoading(false);
                  });
              },
              disabled:
                transactionData?.result.receipt_status !== 'COMPLETED' &&
                transactionData?.result.receipt_status !== 'SKIPPED',
              loading,
            },
          ]}
        />
        <Box sx={{ height: 24 }} />
      </>
    </Page>
  );
}

export { TransactionPage };
